import { useState, useCallback } from 'react';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { configModules, type UnifiedConfigModule } from '@/mocks/adminMonetizationUnified';

type SaveStatus = 'idle' | 'saving' | 'saved';

export default function ConfigurationTabContent() {
  const [modules, setModules] = useState<UnifiedConfigModule[]>(configModules);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');

  const toggleParticipation = (id: string) => {
    setModules((prev) => prev.map((m) => (m.id === id ? { ...m, participating: !m.participating } : m)));
  };

  const handleSave = useCallback(() => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('saved');
      SwalAlert.toast('Configuración guardada correctamente', 'success');
      setTimeout(() => setSaveStatus('idle'), 1500);
    }, 600);
  }, []);

  const activeCount = modules.filter((m) => m.participating).length;
  const totalCount = modules.length;

  return (
    <div className="max-w-[720px] mx-auto space-y-6">
      {/* Stats header */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-amber-500 text-xl"></i>
            </div>
            <div>
              <p className="text-foreground-950 font-bold text-base">{activeCount} de {totalCount} módulos activos</p>
              <p className="text-foreground-400 text-[10px] mt-0.5">
                {activeCount === totalCount ? 'Todos los módulos de monetización están habilitados' : activeCount === 0 ? 'No hay módulos de monetización activos' : 'Gestioná en qué módulos querés participar para generar ingresos'}
              </p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5">
            <span className="w-16 h-1.5 rounded-full bg-background-200 overflow-hidden">
              <span className="block h-full rounded-full bg-amber-500 transition-all duration-500" style={{ width: `${(activeCount / totalCount) * 100}%` }}></span>
            </span>
            <span className="text-foreground-400 text-[10px] font-medium">{Math.round((activeCount / totalCount) * 100)}%</span>
          </div>
        </div>
      </div>

      {/* Modules grid */}
      <div className="space-y-3">
        {modules.map((mod) => (
          <div key={mod.id} className={`bg-background-50 border rounded-2xl p-5 transition-all ${mod.participating ? 'border-background-200' : 'border-background-200 opacity-65'}`}>
            <div className="flex items-start gap-4">
              <div className={`w-11 h-11 rounded-xl ${mod.color} flex items-center justify-center shrink-0 ${!mod.participating ? 'opacity-40' : ''}`}>
                <i className={`${mod.icon} text-background-50 text-lg`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div><h3 className="text-foreground-900 font-semibold text-sm">{mod.label}</h3></div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-semibold ${mod.participating ? 'bg-emerald-100 text-emerald-700' : 'bg-background-200 text-foreground-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${mod.participating ? 'bg-emerald-500' : 'bg-background-400'}`}></span>
                      {mod.participating ? 'Participando' : 'Sin participar'}
                    </span>
                    <div className="flex items-center gap-1.5 bg-background-100 rounded-full px-2 py-1">
                      <span className="text-foreground-600 text-[10px] font-medium">No</span>
                      <button onClick={() => toggleParticipation(mod.id)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${mod.participating ? 'bg-emerald-500' : 'bg-background-300'}`} aria-label={`${mod.participating ? 'Desactivar' : 'Activar'} ${mod.label}`}>
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 shadow-sm transition-all ${mod.participating ? 'left-[22px]' : 'left-0.5'}`}></span>
                      </button>
                      <span className="text-foreground-600 text-[10px] font-medium">Sí</span>
                    </div>
                  </div>
                </div>
                <p className="text-foreground-500 text-[11px] leading-relaxed mt-1.5">{mod.desc}</p>
                {mod.revenueNote && (
                  <div className="flex items-center gap-1.5 mt-2.5">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-background-100 border border-background-200 text-foreground-500 text-[10px] font-medium">
                      <i className="ri-information-line text-[10px]"></i>{mod.revenueNote}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info note */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-amber-200 flex items-center justify-center shrink-0"><i className="ri-lightbulb-line text-amber-600 text-sm"></i></div>
          <div>
            <h4 className="text-foreground-900 font-semibold text-xs mb-1">Información importante</h4>
            <p className="text-foreground-500 text-[10px] leading-relaxed">
              Cada módulo de monetización tiene sus propios términos, comisiones y requisitos. Al activar un módulo, aceptás las condiciones específicas de cada programa. Los cambios en la participación pueden tardar hasta 24 horas en reflejarse completamente en tu perfil público y en los paneles de anunciantes.
            </p>
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div className="bg-background-50 border border-background-200 rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-foreground-800 text-xs font-semibold">{activeCount} de {totalCount} módulos activos</p>
          <p className="text-foreground-400 text-[10px] mt-0.5">Los cambios se aplican al instante en tu perfil de monetización.</p>
        </div>
        <button onClick={handleSave} disabled={saveStatus === 'saving'} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-semibold hover:bg-foreground-800 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap">
          {saveStatus === 'saving' ? (<><i className="ri-loader-4-line animate-spin text-sm"></i>Guardando...</>) : (<><i className="ri-check-line text-sm"></i>Guardar Cambios</>)}
        </button>
      </div>
    </div>
  );
}